/**
 * RTR (Real-Time Response) command queue hooks.
 *
 * Wire contract: see `LOVABLE_TODO.md` (2026-06-06).
 *   POST   /admin/rtr/commands   (X-Smplify-Confirm-Action header)
 *   GET    /admin/rtr/commands
 *   GET    /admin/rtr/commands/:id
 *   PATCH  /admin/rtr/commands/:id/cancel
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { edrGet, edrPatch, edrPost, EdrApiError } from "@/services/edrApi";
import { useConnectorEnabled } from "@/hooks/connectors/useConnector";
import type {
  CreateRtrInput,
  RtrCommandsList,
  RtrState,
  SensorCommand,
} from "@/types/edr-rtr";

const BASE = "/api/v1/connectors/edr/admin/rtr";

export interface RtrCommandsParams {
  deviceId?: string;
  state?: RtrState;
  limit?: number;
}

export function useRtrCommands(params: RtrCommandsParams = {}) {
  const enabled = useConnectorEnabled("edr");
  return useQuery({
    queryKey: ["edr", "rtr", "commands", params],
    enabled,
    refetchInterval: 5_000,
    queryFn: async () => {
      try {
        const res = await edrGet<RtrCommandsList>(`${BASE}/commands`, {
          params: {
            deviceId: params.deviceId,
            state: params.state,
            limit: params.limit ?? 100,
          },
        });
        return res.commands ?? [];
      } catch (e) {
        if (e instanceof EdrApiError && e.status === 404) return [];
        throw e;
      }
    },
  });
}

export function useRtrCommand(id: string | undefined) {
  const enabled = useConnectorEnabled("edr") && !!id;
  return useQuery({
    queryKey: ["edr", "rtr", "command", id],
    enabled,
    refetchInterval: 5_000,
    queryFn: () => edrGet<SensorCommand>(`${BASE}/commands/${id}`),
  });
}

export function useCreateRtrCommand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateRtrInput) => {
      const { confirmAction, ...body } = input;
      return edrPost<SensorCommand>(`${BASE}/commands`, body, {
        headers: { "X-Smplify-Confirm-Action": confirmAction },
      });
    },
    onSuccess: (cmd) => {
      toast.success(`Command queued · ${labelForKind(cmd.kind)}`);
      qc.invalidateQueries({ queryKey: ["edr", "rtr"] });
    },
    onError: (e: Error) =>
      toast.error(e.message || "Could not queue command"),
  });
}

export function useCancelRtrCommand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      edrPatch<SensorCommand>(`${BASE}/commands/${id}/cancel`),
    onSuccess: () => {
      toast.success("Command cancelled");
      qc.invalidateQueries({ queryKey: ["edr", "rtr"] });
    },
    onError: (e: Error) => toast.error(e.message || "Cancel failed"),
  });
}

function labelForKind(k: SensorCommand["kind"]): string {
  switch (k) {
    case "isolate":
      return "Isolate device";
    case "release":
      return "Release isolation";
    case "kill_pid":
      return "Kill process";
    case "quarantine_file":
      return "Quarantine file";
    case "collect_artefact":
      return "Collect artefact";
    case "run_script":
      return "Run script";
  }
}
